import { useState } from "react"
import { mutate } from "swr"
import { i18n } from "@dehub/theme"
import { CustodyTransferApi } from "../../../apis"
import type { ICreateCustodyTransferInput, ICustodyExpectedItem, ICustodyReleasedItem } from "../../../apis/custody-transfer"

// Refresh both the detail query (["custody-transfer", id]) and the list
// (["custody-transfers.list", stage]) — both keys start with "custody-transfer",
// so verifying from a list card updates the card in place.
const invalidate = () =>
  mutate(
    (key) => Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("custody-transfer"),
    undefined,
    { revalidate: true },
  )

// API failures throw an HttpHandler (not an Error subclass), so the old
// `new Error(String(e))` produced "[object Object]". Pull the backend message
// (e.g. "error.custody.not_receiver") off whichever field carries it so the
// dialog's friendlyError() can map it to readable copy.
function toError(e: unknown): Error {
  if (e instanceof Error) return e
  if (e && typeof e === "object") {
    const o = e as { message?: unknown; error?: unknown; code?: unknown }
    const msg = [o.message, o.error, o.code].find(
      (v): v is string => typeof v === "string" && v.length > 0,
    )
    if (msg) return new Error(msg)
  }
  return new Error(typeof e === "string" ? e : i18n.t("delivery:custody.error"))
}

export function useCustodyMutations() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  async function wrap<T>(fn: () => Promise<T>): Promise<T> {
    setSubmitting(true)
    setError(null)
    try {
      const result = await fn()
      await invalidate()
      return result
    } catch (e) {
      setError(toError(e))
      throw e
    } finally {
      setSubmitting(false)
    }
  }

  return {
    submitting,
    error,
    create: (data: ICreateCustodyTransferInput) =>
      wrap(() => CustodyTransferApi.create(data)),
    initiate: (id: string) =>
      wrap(() => CustodyTransferApi.initiate(id)),
    senderVerify: (id: string, body: { pin: string; releasedItems?: ICustodyReleasedItem[] }) =>
      wrap(() => CustodyTransferApi.senderVerify(id, body)),
    receiverVerify: (id: string, body: { pin: string; receivedItems: ICustodyExpectedItem[] }) =>
      wrap(() => CustodyTransferApi.receiverVerify(id, body)),
  }
}
